import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import styles from "./SlidingTabBar.module.css";
import useTabs from "../../hooks/useTabs";
import { Routes } from "../../Routes";
import {
  fetchCertificatesFromFunction,
  getDocumentsForAppID,
} from "../../Models/firebaseHelper";

interface SlidingTabBarItemProps extends SlidingTabBarItemsProps {
  isActive: boolean;
}

function SlidingTabBarItem({
  item,
  onItemClick,
  isActive,
}: SlidingTabBarItemProps) {
  const handleItemClick = useCallback(() => {
    onItemClick(item);
  }, [onItemClick, item]);

  const itemClassName = isActive
    ? `${styles.item} ${styles.itemActive}`
    : styles.item;

  return (
    <button
      type="button"
      className={itemClassName}
      onClick={handleItemClick}
      aria-current={isActive ? "page" : undefined}
      data-tab-id={item.to}
    >
      {item.icon && <span className={styles.icon}>{item.icon}</span>}
      <span className={styles.label}>{item.label}</span>
    </button>
  );
}

function SlidingTabBar(props: SlidingTabBarProps) {
  const isBelowHeader = props.variant === "belowHeader";
  const containerClassName = isBelowHeader
    ? `${styles.container} ${styles.containerBelowHeader}`
    : styles.container;
  const tabbarClassName = isBelowHeader
    ? `${styles.tabbar} ${styles.tabbarBelowHeader}`
    : styles.tabbar;
  const { pathname } = useLocation();
  const { courseId: routeCourseId } = useParams<{ courseId: string }>();
  const { tabs, activeTab } = useTabs(pathname, routeCourseId, props.courseId);
  const { onTabChange } = props;
  const [hasCertificate, setHasCertificate] = useState<boolean | null>(null);
  const [hasDocuments, setHasDocuments] = useState<boolean | null>(null);
  const currentCourseId = props.courseId ?? routeCourseId;

  const onItemClick = useCallback(
    (item: TabItem) => {
      // Mark that user has manually selected a tab for this course
      if (currentCourseId) {
        sessionStorage.setItem(`tabSelected:${currentCourseId}`, item.to);
      }
      onTabChange(item);
    },
    [onTabChange, currentCourseId]
  );

  // Determine if the user has a certificate for this course
  useEffect(() => {
    let isMounted = true;
    async function checkCertificates() {
      try {
        if (!props.courseId) {
          setHasCertificate(false);
          return;
        }
        const res: any = await fetchCertificatesFromFunction(props.courseId);
        const has = Array.isArray(res) ? res.length > 0 : !!res;
        if (isMounted) setHasCertificate(has);
      } catch (error) {
        if (isMounted) setHasCertificate(false);
      }
    }
    checkCertificates();
    return () => {
      isMounted = false;
    };
  }, [props.courseId, props.certificateRefreshKey]);

  // Determine if documents exist for this course
  useEffect(() => {
    let isMounted = true;
    async function checkDocuments() {
      try {
        if (!props.courseId) {
          setHasDocuments(false);
          return;
        }
        const docs = await getDocumentsForAppID(props.courseId);
        if (isMounted) setHasDocuments(docs.length > 0);
      } catch (error) {
        if (isMounted) setHasDocuments(false);
      }
    }
    checkDocuments();
    return () => {
      isMounted = false;
    };
  }, [props.courseId]);

  // Build display order: Certificate first (if present), else hide it
  // Also hide documents tab if no documents exist
  const orderedTabs = useMemo(() => {
    const certSuffix = `/${Routes.Certificate}`;
    const docsSuffix = `/${Routes.Documents}`;
    const certTab = tabs.find((t) => t.to.endsWith(certSuffix));
    const docsTab = tabs.find((t) => t.to.endsWith(docsSuffix));
    let filteredTabs = tabs.filter((t) => {
      // Hide documents tab if no documents exist
      if (t === docsTab && !hasDocuments) return false;
      return true;
    });

    if (hasCertificate && certTab) {
      // Put certificate first
      const others = filteredTabs.filter((t) => t !== certTab);
      return [certTab, ...others];
    }
    // Hide certificate tab when not available
    return filteredTabs.filter((t) => t !== certTab);
  }, [tabs, hasCertificate, hasDocuments]);

  // Auto-navigate to certificate tab only on initial load when available
  useEffect(() => {
    if (!hasCertificate) return;
    if (orderedTabs.length === 0) return;
    const baseTab = tabs[0];
    const certTab = orderedTabs.find((t) =>
      t.to.endsWith(`/${Routes.Certificate}`)
    );

    // Check if user has already manually selected a tab for this course
    const selectedTabKey = `tabSelected:${currentCourseId}`;
    const userSelectedTab = currentCourseId
      ? sessionStorage.getItem(selectedTabKey)
      : null;

    // Only auto-navigate if we're on the base route AND user hasn't manually selected a tab yet
    if (certTab && pathname === baseTab.to && !userSelectedTab) {
      onTabChange(certTab);
      if (currentCourseId) {
        sessionStorage.setItem(selectedTabKey, certTab.to);
      }
    }
  }, [
    hasCertificate,
    pathname,
    orderedTabs,
    onTabChange,
    tabs,
    currentCourseId,
  ]);

  return (
    <div className={containerClassName}>
      <nav className={tabbarClassName} aria-label="Education navigation tabs">
        {orderedTabs.map((item) => (
          <SlidingTabBarItem
            key={item.to}
            item={item}
            onItemClick={onItemClick}
            isActive={item.to === activeTab.to}
          />
        ))}
      </nav>
      {isBelowHeader && (
        <hr className={styles.headerTabDivider} aria-hidden="true" />
      )}
    </div>
  );
}

export default SlidingTabBar;
